'use client';

import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Button } from '@/components/ui/Button';
import {
  simuladorService,
  SimuladorRequest,
  SimuladorResponse,
  AmortizacionRow
} from '@/lib/api/simuladorService';

const tiposConvenio = [
  { value: 'internos', label: 'Caabsa (Internos)' },
  { value: 'externos', label: 'Convenio (Externos)' },
  { value: 'auxiliaresCaabsaEagle', label: 'Auxiliares Caabsa Eagle' },
  { value: 'choferesCaabsaEagle', label: 'Choferes Caabsa Eagle' },
];

const tiposBuro = [
  { value: 'bueno', label: 'Bueno' },
  { value: 'regular', label: 'Regular' },
];

const segurosDisponibles = [
  { value: '25000', label: '$25,000' },
  { value: '50000', label: '$50,000' },
  { value: '75000', label: '$75,000' },
  { value: '100000', label: '$100,000' },
];

const frecuenciasDisponibles = [
  { value: 'Semanal', label: 'Semanal' },
  { value: 'Catorcenal', label: 'Catorcenal' },
  { value: 'Quincenal', label: 'Quincenal' },
  { value: 'Mensual', label: 'Mensual' },
];

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
  }).format(value);
};

const formatPercent = (value: number) => {
  return `${value.toFixed(2)}%`;
};

const SimuladorManagement: React.FC = () => {
  // Form state
  const [tipoConvenio, setTipoConvenio] = useState('internos');
  const [buroCredito, setBuroCredito] = useState('bueno');
  const [seguro, setSeguro] = useState('25000');
  const [frecuencia, setFrecuencia] = useState('Quincenal');
  const [fechaIngreso, setFechaIngreso] = useState('');
  const [antiguedad, setAntiguedad] = useState(0);
  const [ingresos, setIngresos] = useState(0);
  const [montoSolicitado, setMontoSolicitado] = useState(0);
  const [plazoMeses, setPlazoMeses] = useState(12);

  // Results state
  const [resultado, setResultado] = useState<SimuladorResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [showTablaAmortizacion, setShowTablaAmortizacion] = useState(false);

  // Ref para evitar dobles llamadas
  const isSimulating = useRef(false);

  /** Abre ventana de impresión solo con detalles del simulador y tabla de amortización */
  const imprimirSoloTablaYDetalles = useCallback(() => {
    if (!resultado?.tablaAmortizacion) return;
    const totalIntereses = resultado.tablaAmortizacion.reduce((s, r) => s + (r.pagoIntereses ?? 0), 0);
    const totalIvaIntereses = resultado.tablaAmortizacion.reduce((s, r) => s + (r.ivaIntereses ?? 0), 0);
    const totalGeneral = resultado.totalGeneral ?? (resultado.totalAPagar + resultado.seguroTotal);
    const capitalFinanciado = resultado.capitalFinanciado ?? resultado.montoCredito;
    const tasaStr = `${(resultado.tasaAjustada ?? resultado.tasa).toFixed(2)}%`;

    const filasTabla = resultado.tablaAmortizacion
      .map(
        (row: AmortizacionRow) =>
          `<tr>
            <td class="tc">${row.numero}</td>
            <td>${row.desde}</td>
            <td>${row.al}</td>
            <td class="tc">${row.dias}</td>
            <td class="tr">${formatCurrency(row.pagoCapital)}</td>
            <td class="tr">${formatCurrency(row.pagoIntereses ?? 0)}</td>
            <td class="tr">${formatCurrency(row.ivaIntereses ?? 0)}</td>
            <td class="tr">${formatCurrency(row.seguro ?? 0)}</td>
            <td class="tr">${formatCurrency(row.totalPeriodo ?? 0)}</td>
            <td class="tr">${formatCurrency(row.saldoCapital ?? 0)}</td>
          </tr>`
      )
      .join('');

    const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Tabla de Amortizaci\u00f3n - Simulador</title>
  <style>
    body { font-family: system-ui, sans-serif; font-size: 12px; padding: 16px; color: #111; }
    .header { text-align: center; margin-bottom: 20px; }
    .header h1 { font-size: 18px; margin: 0; }
    .header p { margin: 4px 0; }
    .detalles { margin-bottom: 20px; border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px 16px; }
    .detalles h2 { font-size: 14px; margin: 0 0 12px 0; padding-bottom: 8px; border-bottom: 1px solid #e5e7eb; }
    .fila { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #f3f4f6; }
    .fila:last-of-type { border-bottom: none; }
    table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 11px; }
    th, td { border: 1px solid #e5e7eb; padding: 6px 8px; }
    th { background: #f3f4f6; font-weight: 600; }
    .tr { text-align: right; }
    .tc { text-align: center; }
    tfoot td { background: #f3f4f6; font-weight: 600; }
    .footer { margin-top: 16px; font-size: 11px; color: #6b7280; text-align: center; }
    .total-box { margin-top: 12px; padding: 12px; background: #111; color: #fff; border-radius: 8px; display: flex; justify-content: space-between; align-items: center; }
  </style>
</head>
<body>
  <div class="header">
    <h1>MIPYMEX S.A. DE C.V. SOFOM ENR</h1>
    <p><strong>SIMULADOR DE TABLA DE AMORTIZACI\u00d3N</strong></p>
    <p style="font-size: 11px; color: #6b7280;">DE USO EXCLUSIVO PARA PERSONAL DE MIPYMEX</p>
  </div>
  <div class="detalles">
    <h2>Detalle de la simulaci\u00f3n</h2>
    <div class="fila"><span>Fecha:</span><span>${new Date().toLocaleDateString('es-MX')}</span></div>
    <div class="fila"><span>Monto solicitado:</span><span>${formatCurrency(resultado.montoCredito)}</span></div>
    <div class="fila"><span>Monto financiado:</span><span>${formatCurrency(capitalFinanciado)}</span></div>
    <div class="fila"><span>Plazo:</span><span>${resultado.plazoMeses} meses</span></div>
    <div class="fila"><span>Frecuencia:</span><span>${frecuencia}</span></div>
    <div class="fila"><span>N\u00famero de pagos:</span><span>${resultado.numPagos}</span></div>
    <div class="fila"><span>Tasa de inter\u00e9s:</span><span>${tasaStr} Anual</span></div>
    <div class="fila"><span>Comisi\u00f3n (IVA incl.):</span><span>${formatCurrency(resultado.montoComision + resultado.ivaComision)}</span></div>
    <div class="total-box">
      <span>Total a pagar:</span>
      <span style="font-size: 18px; font-weight: bold;">${formatCurrency(totalGeneral)}</span>
    </div>
  </div>
  <table>
    <thead>
      <tr>
        <th class="tc">#</th>
        <th>Desde</th>
        <th>Al</th>
        <th class="tc">D\u00edas</th>
        <th class="tr">Pago a Capital</th>
        <th class="tr">Pago a Intereses</th>
        <th class="tr">IVA Intereses</th>
        <th class="tr">Seguro</th>
        <th class="tr">Total Periodo</th>
        <th class="tr">Saldo Capital</th>
      </tr>
    </thead>
    <tbody>${filasTabla}</tbody>
    <tfoot>
      <tr>
        <td colspan="4" class="tr">TOTAL</td>
        <td class="tr">${formatCurrency(capitalFinanciado)}</td>
        <td class="tr">${formatCurrency(totalIntereses)}</td>
        <td class="tr">${formatCurrency(totalIvaIntereses)}</td>
        <td class="tr">${formatCurrency(resultado.seguroTotal)}</td>
        <td class="tr">${formatCurrency(totalGeneral)}</td>
        <td class="tc">-</td>
      </tr>
    </tfoot>
  </table>
  <div class="footer">
    <p><strong>Los datos presentados en esta simulaci\u00f3n pueden diferir conforme a las condiciones reales del cr\u00e9dito.</strong></p>
    <p>El monto puede variar dependiendo de tu historial crediticio.</p>
    <p style="margin-top: 8px; font-weight: bold;">SUJETO A APROBACI\u00d3N DE CR\u00c9DITO</p>
  </div>
  <script>
    window.onload = function() { window.print(); };
  </script>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const ventana = window.open(url, '_blank', 'noopener,noreferrer');
    if (ventana) {
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    }
  }, [resultado, frecuencia]);

  // Calcular antiguedad a partir de fecha de ingreso (igual que legacy)
  useEffect(() => {
    if (fechaIngreso) {
      const fechaIng = new Date(fechaIngreso + 'T06:00:00');
      const hoy = new Date();

      // Cálculo igual al legacy: monthDiff + 1
      let meses = (hoy.getFullYear() - fechaIng.getFullYear()) * 12;
      meses -= fechaIng.getMonth() + 1;
      meses += hoy.getMonth();
      meses = meses <= 0 ? 0 : meses;
      meses += 1; // legacy suma 1

      setAntiguedad(Math.max(0, meses));
    }
  }, [fechaIngreso]);

  // Deshabilitar ingresos para Eagle
  const isEagle = tipoConvenio === 'auxiliaresCaabsaEagle' || tipoConvenio === 'choferesCaabsaEagle';

  // Ejecutar simulacion
  const ejecutarSimulacion = useCallback(async () => {
    if (isSimulating.current) return;
    isSimulating.current = true;
    setLoading(true);
    setError(null);

    const request: SimuladorRequest = {
      tipoConvenio,
      buroCredito,
      seguro,
      frecuencia,
      antiguedad,
      ingresos: isEagle ? 0 : ingresos,
      montoSolicitado,
      plazoMeses,
    };

    const response = await simuladorService.simular(request);

    if (response.error) {
      setError(response.error);
      setResultado(null);
    } else if (response.data) {
      if (response.data.error) {
        setError(response.data.error);
        setResultado(null);
      } else {
        setError(null);
        setResultado(response.data);
        // Auto-ajustar monto al máximo si no hay monto seleccionado o es menor al mínimo
        if ((montoSolicitado === 0 || montoSolicitado < 3000) && response.data.montoMaximo > 0) {
          setMontoSolicitado(response.data.montoMaximo);
        }
      }
    }

    setLoading(false);
    isSimulating.current = false;
  }, [tipoConvenio, buroCredito, seguro, frecuencia, antiguedad, ingresos, montoSolicitado, plazoMeses, isEagle]);

  // Auto-simular cuando cambian parámetros clave (reactivo como el legacy)
  useEffect(() => {
    // Solo simular si hay datos suficientes
    const tieneAntiguedad = antiguedad >= 6;
    const tieneDatosBase = isEagle || ingresos > 0;

    if (tieneAntiguedad && tieneDatosBase) {
      ejecutarSimulacion();
    } else if (antiguedad > 0 && antiguedad < 6) {
      setError('Lo sentimos, minimo requieres tener 6 meses de antiguedad');
      setResultado(null);
    }
  }, [tipoConvenio, buroCredito, frecuencia, antiguedad, ingresos, isEagle]);

  // Re-simular cuando cambia el plazo
  useEffect(() => {
    if (resultado && antiguedad >= 6) {
      ejecutarSimulacion();
    }
  }, [plazoMeses]);

  // Re-simular cuando cambia el monto (con pequeño debounce para el slider)
  useEffect(() => {
    if (resultado && antiguedad >= 6 && montoSolicitado > 0) {
      const timer = setTimeout(() => {
        ejecutarSimulacion();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [montoSolicitado]);

  // Re-simular cuando cambia el seguro
  useEffect(() => {
    if (resultado && antiguedad >= 6) {
      ejecutarSimulacion();
    }
  }, [seguro]);

  // Monto máximo del slider
  const montoMaximoSlider = resultado?.montoMaximo || 100000;
  const montoMinimoSlider = 3000;

  // Handler para el slider - mínimo $3,000
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valor = Number(e.target.value);
    setMontoSolicitado(Math.max(montoMinimoSlider, valor));
  };

  // Plazos disponibles
  const plazosDisponibles = resultado?.plazosDisponibles || [6, 12, 18, 24, 30, 36, 42, 48];

  // Reset cuando cambia tipo de convenio
  const handleTipoConvenioChange = (value: string) => {
    setTipoConvenio(value);
    setResultado(null);
    setError(null);
    setMontoSolicitado(0);
    if (value.includes('Eagle')) {
      setIngresos(0);
    }
  };

  // Obtener etiqueta de frecuencia para el mensaje
  const getFrecuenciaLabel = () => {
    if (frecuencia === 'Semanal') return 'semanales';
    if (frecuencia === 'Catorcenal') return 'catorcenales';
    if (frecuencia === 'Mensual') return 'mensuales';
    return 'quincenales';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Simulador de Credito</h1>
          <p className="text-sm text-gray-500 mt-1">
            Cotiza tu credito facil, rapido y seguro.
          </p>
        </div>
        {loading && (
          <span className="text-sm text-blue-600 animate-pulse">Calculando...</span>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Panel de opciones */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-5">
          <h2 className="text-lg font-semibold text-gray-800 border-b pb-2">Cotiza tu credito</h2>

          {/* Tipo de Convenio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Cliente</label>
            <select
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              value={tipoConvenio}
              onChange={(e) => handleTipoConvenioChange(e.target.value)}
            >
              {tiposConvenio.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Fecha de Ingreso */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de Ingreso</label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              value={fechaIngreso}
              max={new Date().toISOString().split('T')[0]}
              onChange={(e) => setFechaIngreso(e.target.value)}
            />
            {antiguedad > 0 && (
              <p className={`text-xs mt-1 ${antiguedad < 6 ? 'text-red-600' : 'text-gray-500'}`}>
                Antiguedad: <span className="font-semibold">{antiguedad} meses</span>
                {antiguedad < 6 && ' (minimo 6 meses)'}
              </p>
            )}
          </div>

          {/* Ingresos */}
          {!isEagle && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ingresos Mensuales</label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500">$</span>
                <input
                  type="number"
                  className="w-full pl-7 pr-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  value={ingresos || ''}
                  onChange={(e) => setIngresos(Number(e.target.value))}
                  placeholder="0"
                  min="0"
                />
              </div>
              {resultado?.esDirectivo && (
                <p className="text-xs text-green-600 mt-1 font-medium">
                  Aplica condiciones de Directivo
                </p>
              )}
            </div>
          )}

          {/* Buro de Credito */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Buro de Credito</label>
            <div className="flex gap-6">
              {tiposBuro.map((b) => (
                <label key={b.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="buro"
                    value={b.value}
                    checked={buroCredito === b.value}
                    onChange={(e) => setBuroCredito(e.target.value)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">{b.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Seguro */}
          {!isEagle && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Seguro</label>
              <div className="flex flex-wrap gap-4">
                {segurosDisponibles.map((s) => (
                  <label key={s.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="seguro"
                      value={s.value}
                      checked={seguro === s.value}
                      onChange={(e) => setSeguro(e.target.value)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm">{s.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Plazo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Plazo (meses)</label>
            <div className="flex flex-wrap gap-2">
              {plazosDisponibles.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPlazoMeses(p)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    plazoMeses === p
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Frecuencia */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Recibes tu nomina</label>
            <div className="flex flex-wrap gap-4">
              {frecuenciasDisponibles.map((f) => (
                <label key={f.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="frecuencia"
                    value={f.value}
                    checked={frecuencia === f.value}
                    onChange={(e) => setFrecuencia(e.target.value)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">{f.label.toLowerCase()}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Panel de resultados */}
        <div className="space-y-6">
          {/* Monto y Slider */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="text-center mb-4">
              <p className="text-sm text-gray-500">Puedes solicitar hasta:</p>
              <p className="text-3xl font-bold text-blue-600">{formatCurrency(montoMaximoSlider)}</p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2 text-center">
                Monto del prestamo:
              </label>
              <p className="text-2xl font-bold text-gray-900 text-center mb-3">
                {formatCurrency(Math.max(montoMinimoSlider, montoSolicitado))}
              </p>
              <input
                type="range"
                min={montoMinimoSlider}
                max={montoMaximoSlider}
                step="1000"
                value={Math.max(montoMinimoSlider, montoSolicitado)}
                onChange={handleSliderChange}
                disabled={!resultado || montoMaximoSlider <= montoMinimoSlider}
                className="w-full h-3 bg-green-200 rounded-lg appearance-none cursor-pointer accent-green-600 disabled:opacity-50"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>{formatCurrency(montoMinimoSlider)}</span>
                <span>{formatCurrency(montoMaximoSlider)}</span>
              </div>
              <p className="text-xs text-gray-500 text-center mt-2">
                Mueve la barra verde para modificar el monto del prestamo.
              </p>
            </div>

            {/* Pago destacado */}
            {resultado && !resultado.error && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <p className="text-sm text-blue-700">
                  Realizaras <span className="font-bold">{resultado.numPagos}</span> pagos {getFrecuenciaLabel()} de:
                </p>
                <p className="text-4xl font-bold text-blue-700 mt-1">
                  {formatCurrency(resultado.pagoTotalParcialidad)}
                </p>
              </div>
            )}
          </div>

          {/* Detalle de Simulación */}
          {resultado && !resultado.error && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-800 border-b pb-2">Detalle de la Simulacion</h2>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span className="text-gray-600">Fecha de Inicio:</span>
                  <span className="font-medium">{new Date().toLocaleDateString('es-MX')}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span className="text-gray-600">Monto Solicitado:</span>
                  <span className="font-medium">{formatCurrency(resultado.montoCredito)}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span className="text-gray-600">Plazo:</span>
                  <span className="font-medium">{resultado.plazoMeses} meses</span>
                </div>
                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span className="text-gray-600">Frecuencia de pago:</span>
                  <span className="font-medium">{frecuencia}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span className="text-gray-600">Numero de pagos:</span>
                  <span className="font-medium">{resultado.numPagos}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span className="text-gray-600">Monto Financiado:</span>
                  <span className="font-medium">{formatCurrency(resultado.capitalFinanciado || resultado.montoCredito)}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span className="text-gray-600">Tasa de Interes Simple:</span>
                  <span className="font-medium">{formatPercent(resultado.tasaAjustada || resultado.tasa)} Anual</span>
                </div>
                <div className="py-2 text-gray-600 text-xs">
                  El Monto Financiado incluye una comision de: <span className="font-semibold">{formatCurrency(resultado.montoComision + resultado.ivaComision)}</span> IVA incluido
                </div>
              </div>

              <div className="flex justify-between items-center p-4 bg-gray-900 text-white rounded-lg">
                <span className="text-lg">Total a pagar:</span>
                <span className="text-2xl font-bold">{formatCurrency(resultado.totalGeneral || (resultado.totalAPagar + resultado.seguroTotal))}</span>
              </div>

              <Button
                variant="secondary"
                className="w-full"
                onClick={() => setShowTablaAmortizacion(!showTablaAmortizacion)}
              >
                {showTablaAmortizacion ? 'Ocultar' : 'Ver'} Tabla de Amortizacion
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Tabla de Amortizacion */}
      {showTablaAmortizacion && resultado?.tablaAmortizacion && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 print:shadow-none print:border-none">
          <div className="flex justify-between items-center mb-4 print:hidden">
            <h2 className="text-lg font-semibold text-gray-800">Tabla de Amortizacion</h2>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setShowTablaAmortizacion(false)}>
                Regresar
              </Button>
              <Button variant="primary" onClick={imprimirSoloTablaYDetalles}>
                Imprimir
              </Button>
            </div>
          </div>

          {/* Header para impresión */}
          <div className="hidden print:block mb-4 text-center">
            <h1 className="text-xl font-bold">MIPYMEX S.A. DE C.V. SOFOM ENR</h1>
            <p>SIMULADOR DE TABLA DE AMORTIZACION</p>
            <p className="text-sm text-gray-500">DE USO EXCLUSIVO PARA PERSONAL DE MIPYMEX</p>
          </div>

          {/* Resumen para impresión */}
          <div className="hidden print:block mb-4 text-sm">
            <p><strong>Fecha:</strong> {new Date().toLocaleDateString()}</p>
            <p><strong>Monto Solicitado:</strong> {formatCurrency(resultado.montoCredito)}</p>
            <p><strong>Monto Financiado:</strong> {formatCurrency(resultado.capitalFinanciado || resultado.montoCredito)}</p>
            <p><strong>Plazo:</strong> {resultado.plazoMeses} meses</p>
            <p><strong>Frecuencia:</strong> {frecuencia}</p>
            <p><strong>Tasa:</strong> {formatPercent(resultado.tasaAjustada || resultado.tasa)} Anual</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-1 py-2 text-center font-semibold">#</th>
                  <th className="px-1 py-2 text-left font-semibold">Desde</th>
                  <th className="px-1 py-2 text-left font-semibold">Al</th>
                  <th className="px-1 py-2 text-center font-semibold">Dias</th>
                  <th className="px-1 py-2 text-right font-semibold">Pago a Capital</th>
                  <th className="px-1 py-2 text-right font-semibold">Pago a Intereses</th>
                  <th className="px-1 py-2 text-right font-semibold">IVA Intereses</th>
                  <th className="px-1 py-2 text-right font-semibold">Seguro</th>
                  <th className="px-1 py-2 text-right font-semibold">Total Periodo</th>
                  <th className="px-1 py-2 text-right font-semibold">Saldo Capital</th>
                </tr>
              </thead>
              <tbody>
                {resultado.tablaAmortizacion.map((row: any, index: number) => (
                  <tr key={row.numero || index} className="border-b hover:bg-gray-50">
                    <td className="px-1 py-1 text-center">{row.numero || index + 1}</td>
                    <td className="px-1 py-1">{row.desde || row.fecha || '-'}</td>
                    <td className="px-1 py-1">{row.al || '-'}</td>
                    <td className="px-1 py-1 text-center">{row.dias || '-'}</td>
                    <td className="px-1 py-1 text-right">{formatCurrency(row.pagoCapital ?? row.capital ?? 0)}</td>
                    <td className="px-1 py-1 text-right">{formatCurrency(row.pagoIntereses ?? row.interes ?? 0)}</td>
                    <td className="px-1 py-1 text-right">{formatCurrency(row.ivaIntereses ?? row.ivaInteres ?? 0)}</td>
                    <td className="px-1 py-1 text-right">{formatCurrency(row.seguro ?? 0)}</td>
                    <td className="px-1 py-1 text-right font-medium">{formatCurrency(row.totalPeriodo ?? row.pagoTotal ?? 0)}</td>
                    <td className="px-1 py-1 text-right">{formatCurrency(row.saldoCapital ?? row.saldo ?? 0)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-100 font-semibold">
                  <td colSpan={4} className="px-1 py-2 text-right">TOTAL</td>
                  <td className="px-1 py-2 text-right">{formatCurrency(resultado.capitalFinanciado || resultado.montoCredito)}</td>
                  <td className="px-1 py-2 text-right">{formatCurrency(resultado.tablaAmortizacion.reduce((s, r) => s + (r.pagoIntereses ?? 0), 0))}</td>
                  <td className="px-1 py-2 text-right">{formatCurrency(resultado.tablaAmortizacion.reduce((s, r) => s + (r.ivaIntereses ?? 0), 0))}</td>
                  <td className="px-1 py-2 text-right">{formatCurrency(resultado.seguroTotal)}</td>
                  <td className="px-1 py-2 text-right">{formatCurrency(resultado.totalGeneral || (resultado.totalAPagar + resultado.seguroTotal))}</td>
                  <td className="px-1 py-2 text-center">-</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="mt-4 text-xs text-gray-500 text-center">
            <p className="font-semibold">Los datos presentados en esta simulacion pueden diferir conforme a las condiciones reales del credito.</p>
            <p>El monto puede variar dependiendo de tu historial crediticio.</p>
            <p className="font-bold mt-1">SUJETO A APROBACION DE CREDITO</p>
          </div>
        </div>
      )}

      {/* Contacto */}
      <div className="bg-blue-50 rounded-xl p-6 text-center print:hidden">
        <h3 className="font-semibold text-blue-800 mb-2">Contactanos</h3>
        <p className="text-sm text-blue-700">
          Correo: <a href="mailto:infonomina@mipymex.mx" className="underline">infonomina@mipymex.mx</a>
        </p>
        <p className="text-sm text-blue-700">
          Telefono: <span className="font-semibold">7590 2100 ext 2149</span>
        </p>
      </div>
    </div>
  );
};

export default SimuladorManagement;
